import React, { ElementType, useState } from "react";
import { Button } from "../Button";
import Select from "react-select";
import { ILinkFunc } from "../../lib/util";
import { IMediaItem } from "../../lib";
import Heading from "./Heading";

const selectStyles: any = {
  control: (base: object) => ({
    ...base,
    borderColor: "transparent",
    background: "transparent",
    borderBottomColor: "var(--color-accent)",
    borderRadius: 0,
  }),
  singleValue: (base: object) => ({
    ...base,
    color: "var(--color-body-text-inverted)",
  }),
  menu: (base: object) => ({
    ...base,
    color: "var(--color-body-text)",
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    paddingRight: "0px",
    color: "var(--color-accent)",
  }),
  indicatorSeparator: (base: object) => ({
    ...base,
    display: "none",
  }),
  placeholder: (base: object) => ({
    ...base,
    color: "var(--color-body-text-inverted)",
    whiteSpace: "nowrap",
  }),
  valueContainer: (base: object) => ({
    ...base,
    paddingLeft: 0,
  }),
};

export interface ICommunitiesProps {
  className?: string;
  title?: string;
  /**
   * Header text/title to render, if any.
   */
  titleHeadingLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";
  linkFunc?: ILinkFunc;
  communities: ICommunityInfo[];
  displayPagination: boolean;
  scaleImageOnHover?: boolean;
}

export interface ICommunityInfo {
  title: string;
  image?: IMediaItem;
  description: string;
  links?: { label: string; url: string }[];
  card?: ICommunityCard;
}

export interface ICommunityCard {
  image: IMediaItem;
  primaryText: string;
  secondaryText: string;
}

export const Communities: React.FC<ICommunitiesProps> = ({
  className,
  title,
  titleHeadingLevel = "h3",
  linkFunc,
  communities,
  displayPagination = false,
  scaleImageOnHover = false,
}) => {
  type ISelectOptionType = {
    value: number;
    label: string;
  };

  const communityOpts: ISelectOptionType[] = communities.map((item, index) => ({
    value: index,
    label: item.title,
  }));

  const [community, setCommunity] = useState<ISelectOptionType>(
    communityOpts[0]
  );

  const communityIndexOnChange = (option: any) => {
    setCommunity(option);
  };

  const linkf = linkFunc || ((props) => <a {...props} />);

  const currentCommunity = communities.length && communities[community.value];

  const communitiesCount = `${communityOpts.length}`.padStart(2, "0")

  if (!currentCommunity) return <></>;

  return (
    <div className={`${className} !overflow-hidden communities flex flex-wrap md:flex-nowrap`}>
      {currentCommunity.image?.url && (
        <img
          src={currentCommunity.image.url}
          width={currentCommunity.image.dimensions?.[0]}
          height={currentCommunity.image.dimensions?.[1]}
          alt=""
          className={`object-cover ${scaleImageOnHover ? 'transition duration-300 hover:scale-110' : ''}`}
        />
      )}
      <div className="p-5 flex flex-col gap-5 justify-center items-center inverted">
        <Heading
          headingLevel={titleHeadingLevel}
          className="text-3xl md:text-5xl uppercase"
        >
          {title}
        </Heading>
        {communities.length > 1 && (
          <div className="w-full flex flex-row flex-wrap justify-between items-baseline">
            <label htmlFor="community">
              <span className="sr-only">Select community</span>
              <Select
                name="market"
                value={community}
                onChange={(option) => communityIndexOnChange(option)}
                options={communityOpts}
                styles={selectStyles}
              />
            </label>
            {displayPagination && (<span className="block">
              {`${`${community.value + 1}`.padStart(2, "0")}/${communitiesCount}`}
            </span>)}
          </div>
        )}
        {currentCommunity.description && (
          <div
            dangerouslySetInnerHTML={{ __html: currentCommunity.description }}
          />
        )}
        {currentCommunity.links && (
          <div className="flex gap-3 flex-wrap justify-center">
            {currentCommunity.links.map((link, index) => (
              <Button
                icon={false}
                linkProps={{
                  href: link.url,
                  key: index,
                  className: `min-w-[140px]`,
                }}
                linkFunc={linkf}
                key={index}
              >
                {link.label}
              </Button>
            ))}
          </div>
        )}
        {currentCommunity.card && (
          <div className="community-card flex flex-wrap gap-5 sm:gap-0 sm:flex-nowrap relative">
            {currentCommunity.card.image?.url && (
              <img
                src={currentCommunity.card.image.url}
                className="shrink w-full sm:max-w-[50%]"
              />
            )}
            <div className="flex flex-col grow gap-5 justify-center items-center">
              <span className="text-5xl">
                {currentCommunity.card.primaryText}
              </span>
              <hr className="divide-white w-16" />
              <span>{currentCommunity.card.secondaryText}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Communities;
